import { ButtonLoaderIcon, space, tok, tokPx } from '@sunwebgroup/ui-components';

import { Button, TextField, Typography } from '@sunwebgroup/ui-components/uds';

import ServiceBox from '@/scopes/checkout/components/services/ServiceBox';

import { useIntl } from '@/intl';

import { Controller, useForm } from 'react-hook-form';
import { useCouponCode } from '@/scopes/checkout/domain/hooks';
import { styled } from 'styled-components';
import * as React from 'react';
import { useCouponStore } from './store';

interface FormValues {
  couponCode: string;
}

const FormWrapper = styled.form`
  display: flex;
  gap: ${space('primitives_space_16')};
`;

const SubmitButton = styled(Button)`
  width: ${tokPx('primitives_area_size_160')};
`;

const CouponTextFieldWrapper = styled.div`
  width: ${tokPx('primitives_area_size_320')};
`;

const CouponFeedbackText = styled(Typography)`
  color: ${tok('primitives_color_green_350')};
`;

export function CouponCodeForm() {
  const intl = useIntl();
  const { setCouponCode } = useCouponCode();

  const [isChecking, setIsChecking] = React.useState(false);

  const onSubmit = (result: FormValues) => {
    setIsChecking(true);
    setCouponCode(result.couponCode);
  };

  const couponInfo = useCouponStore((state) => state.couponInfo);

  React.useEffect(() => {
    if (couponInfo?.success) {
      setIsChecking(false);
    } else if (couponInfo?.success === false) {
      setIsChecking(false);
    }
  }, [couponInfo]);

  const { control, handleSubmit } = useForm<FormValues>();

  // Determine status without nested ternary
  let status: 'error' | 'success' | undefined;
  if (couponInfo != null && !couponInfo.success) {
    status = 'error';
  } else if (couponInfo != null && couponInfo.success) {
    status = 'success';
  } else {
    status = undefined;
  }

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_coupon_code_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_coupon_code_description' })}
      glyph="TicketWithPercentage"
    >
      <FormWrapper onSubmit={handleSubmit(onSubmit)}>
        <CouponTextFieldWrapper>
          <Controller
            name="couponCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                status={status}
                disabled={isChecking}
                feedbackText={
                  couponInfo != null && !couponInfo.success
                    ? intl.formatMessage({ id: 'checkout_steps_coupon_code_error' })
                    : undefined
                }
              />
            )}
          />
          {couponInfo != null && couponInfo.success && (
            <CouponFeedbackText $variant="body-s-regular-loose">
              {intl.formatMessage({ id: 'checkout_steps_coupon_code_success' })}
            </CouponFeedbackText>
          )}
        </CouponTextFieldWrapper>
        <SubmitButton $variant="secondary" type="submit" disabled={isChecking}>
          {isChecking ? (
            <ButtonLoaderIcon />
          ) : (
            intl.formatMessage({ id: 'checkout_steps_participants_badge_form_submit_button' })
          )}
        </SubmitButton>
      </FormWrapper>
    </ServiceBox>
  );
}
